import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Upload, MapPin, Clock, User, Mail, Phone, FileText } from "lucide-react";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { useContent } from "@/hooks/useContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESUME_MB = 10;
const ALLOWED_RESUME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_RESUME_EXT = [".pdf", ".doc", ".docx"];

const RESPONSIBILITY_GROUPS = [
  {
    title: "Office Operations",
    items: [
      "Manage phone, email, mail, supplies, the Temple calendar and routine office operations.",
      "Support the rabbi and leadership with scheduling, documents, research and congregational communications; attend monthly evening Board meetings.",
    ],
  },
  {
    title: "Membership & Congregational Support",
    items: [
      "Serve as the primary contact for members, prospective members and visitors - including tours, applications, immediate needs and follow-up.",
      "Coordinate with prospective and new members and the Membership Committee through sign-up and application, entering all information in the Temple Management Software.",
      "Create new members' Temple Management Software accounts and manage each account.",
      "Maintain accurate member and prospect records and prepare information for the rabbi and leadership.",
    ],
  },
  {
    title: "Services, Events & Holidays",
    items: [
      "Coordinate administrative and on-site support for services, holidays, programs, life-cycle events and rentals - rooms, cleaning, food, deliveries, technology and vendors.",
      "Manage High Holy Day and holiday logistics: attendance lists, schedules, booklets, forms, mailings, supplies and volunteer coordination.",
      "Reconcile registrations, donations and payments in the Temple Management Software; maintain check-in lists and event cash boxes and assist with payment processing.",
    ],
  },
  {
    title: "Facilities, Security & Vendors",
    items: [
      "Coordinate facility use, cleaning, maintenance, repairs, office technology and vendors.",
      "Coordinate with the security provider on scheduling, changes and payments.",
      "Report technical, facility and operational issues and work with Temple leadership and vendors on resolution.",
    ],
  },
];

const QUALIFICATIONS = [
  "Minimum high school diploma or equivalent and 2-3 years of prior office or administrative experience.",
  "Strong organization, time management and written, verbal and interpersonal communication skills.",
  "Proficiency with Microsoft Office, email, social media and database or Temple Management Software.",
  "Professional judgment, discretion and sensitivity to Jewish customs, holidays and Reform practices; Hebrew knowledge is helpful but not required.",
  "Friendly, dependable and adaptable, with the ability to work independently and collaboratively.",
];

export default function Hiring() {
  const { body: adminBody, loading } = useContent("hiring.body", "");
  const hasAdminContent = !loading && !!(adminBody && adminBody.trim() && adminBody !== "<p></p>");

  return (
    <>
      <SEOHead
        title="We're Hiring - Temple Office Administrator"
        description="Temple Israel of Brevard is hiring a Temple Office Administrator. Join our welcoming Reform Jewish community and support our members, events, and daily operations."
        path="/hiring"
      />
      <PageHero
        eyebrow="We're Hiring"
        title="Temple Office Administrator"
        subtitle="Join our welcoming community. Support our members, events, and daily operations."
      />

      <section className="bg-white py-12 lg:py-16" data-testid="hiring-content">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          {hasAdminContent ? (
            <div className="prose prose-lg max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: adminBody }} />
          ) : (
            <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-10 lg:gap-16 items-start">
              {/* Job details */}
              <div className="max-w-none">
                <h2 className="font-display text-3xl md:text-4xl text-[var(--blue-ink)] mb-4">Position Summary</h2>
                <p className="text-[var(--ink)] leading-relaxed mb-8">
                  The Temple Office Administrator manages daily operations and serves as a
                  welcoming point of contact for the rabbi, lay leadership, members, prospective
                  members and visitors. The role coordinates records, communications, events,
                  facilities and vendors while handling confidential information with discretion.
                </p>

                <h3 className="font-display text-2xl text-[var(--blue-ink)] mb-3">Key Responsibilities</h3>
                <div className="space-y-6 mb-8">
                  {RESPONSIBILITY_GROUPS.map((group) => (
                    <div key={group.title}>
                      <h4 className="font-semibold text-[var(--blue-deep)] mb-2">{group.title}</h4>
                      <ul className="space-y-2 text-[var(--ink)]">
                        {group.items.map((li, i) => (
                          <li key={i} className="flex gap-2 leading-relaxed">
                            <CheckCircle2 size={16} className="text-[var(--honey)] flex-shrink-0 mt-1" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <h3 className="font-display text-2xl text-[var(--blue-ink)] mb-3">Qualifications</h3>
                <ul className="space-y-2 text-[var(--ink)] mb-8">
                  {QUALIFICATIONS.map((q, i) => (
                    <li key={i} className="flex gap-2 leading-relaxed">
                      <CheckCircle2 size={16} className="text-[var(--honey)] flex-shrink-0 mt-1" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="rounded-2xl bg-[var(--cream)] p-5 border border-[var(--line)]">
                    <div className="flex items-center gap-2 text-[var(--blue-deep)] font-semibold mb-1">
                      <Clock size={16} /> Schedule
                    </div>
                    <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
                      Part-time - four days per week, seven hours per day (28 hours per week).
                      Specific weekdays and daily hours will be set by Temple leadership.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[var(--cream)] p-5 border border-[var(--line)]">
                    <div className="flex items-center gap-2 text-[var(--blue-deep)] font-semibold mb-1">
                      <User size={16} /> Reporting
                    </div>
                    <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
                      Reports to the Temple President or the President's designee.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl bg-white p-5 border border-[var(--line)]">
                  <div className="flex items-center gap-2 text-[var(--blue-deep)] font-semibold mb-1">
                    <MapPin size={16} /> Location
                  </div>
                  <p className="text-sm text-[var(--ink-soft)]">
                    Temple Israel of Brevard - 7300 Lake Andrew Dr, Melbourne, FL 32940
                  </p>
                </div>
              </div>

              {/* Application form */}
              <HiringApplyForm />
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function HiringApplyForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [resume, setResume] = useState(null);
  const [resumeError, setResumeError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onResumeChange = (e) => {
    setResumeError("");
    const f = e.target.files?.[0];
    if (!f) { setResume(null); return; }
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    const okType = ALLOWED_RESUME_TYPES.includes(f.type) || ALLOWED_RESUME_EXT.includes(ext);
    if (!okType) {
      setResumeError("Resume must be a Word document (.doc / .docx) or PDF.");
      setResume(null); return;
    }
    if (f.size > MAX_RESUME_MB * 1024 * 1024) {
      setResumeError(`Resume is over ${MAX_RESUME_MB} MB. Please compress or send a smaller file.`);
      setResume(null); return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resume) { setResumeError("Please attach your resume."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("position", "Temple Office Administrator");
      fd.append("resume", resume);
      await axios.post(`${API}/hiring/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again or email tioffice@tiofbrevard.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="rounded-2xl bg-[var(--cream)] p-6 md:p-8 border border-[var(--line)] shadow-md sticky top-24"
        data-testid="hiring-apply-success"
      >
        <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
          <CheckCircle2 size={20} /> Application received
        </div>
        <h3 className="font-display text-2xl text-[var(--blue-ink)] mb-3">Todah rabah!</h3>
        <p className="text-[var(--ink)] leading-relaxed">
          Thank you for applying. We've saved your resume and notified our office.
          You'll hear from us soon. Any questions in the meantime? Email{" "}
          <a href="mailto:tioffice@tiofbrevard.com" className="text-[var(--blue-deep)] underline">
            tioffice@tiofbrevard.com
          </a>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl bg-white p-6 md:p-7 border border-[var(--line)] shadow-lg sticky top-24"
      data-testid="hiring-apply-form"
      encType="multipart/form-data"
    >
      <h3 className="font-display text-2xl text-[var(--blue-ink)] mb-1">Apply now</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-5">Fill in your details and attach your resume - it's that simple.</p>

      <label className="block mb-4">
        <span className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1.5 mb-1"><User size={13}/> Full name</span>
        <input
          type="text" required value={form.name} onChange={setField("name")}
          className="w-full rounded-lg border border-[var(--line)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/50"
          placeholder="Jane Cohen" data-testid="hiring-input-name"
        />
      </label>

      <label className="block mb-4">
        <span className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1.5 mb-1"><Mail size={13}/> Email</span>
        <input
          type="email" required value={form.email} onChange={setField("email")}
          className="w-full rounded-lg border border-[var(--line)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/50"
          placeholder="you@example.com" data-testid="hiring-input-email"
        />
      </label>

      <label className="block mb-4">
        <span className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1.5 mb-1"><Phone size={13}/> Phone</span>
        <input
          type="tel" required value={form.phone} onChange={setField("phone")}
          className="w-full rounded-lg border border-[var(--line)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/50"
          placeholder="(321) 555-0000" data-testid="hiring-input-phone"
        />
      </label>

      <label className="block mb-5">
        <span className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1.5 mb-1"><FileText size={13}/> Resume (Word or PDF, max {MAX_RESUME_MB} MB)</span>
        <div className="mt-1 flex items-center gap-3">
          <label className="btn-ghost cursor-pointer text-sm" data-testid="hiring-input-resume-label">
            <Upload size={14} /> {resume ? "Choose different file" : "Choose file"}
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onResumeChange}
              className="sr-only"
              required
              data-testid="hiring-input-resume"
            />
          </label>
          {resume && (
            <span className="text-xs text-[var(--ink-soft)] truncate" data-testid="hiring-resume-filename">
              {resume.name}
            </span>
          )}
        </div>
        {resumeError && (
          <p className="text-xs text-red-700 mt-2" data-testid="hiring-resume-error">{resumeError}</p>
        )}
      </label>

      {error && (
        <p className="text-sm text-red-700 mb-3" data-testid="hiring-submit-error">{error}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full justify-center disabled:opacity-60"
        data-testid="hiring-submit"
      >
        {submitting ? "Sending..." : "Submit application"}
      </button>

      <p className="text-[11px] text-[var(--muted)] mt-4 text-center">
        Your resume is stored securely and only shared with the temple leadership.
      </p>
    </form>
  );
}
